import * as React from 'react';
import {
  Radio,
  FlaskConical,
  Database,
  FileInput,
  User,
  Rewind,
  HelpCircle,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../primitives/tooltip';
import { Badge } from '../primitives/badge';
import { cn } from '../lib/utils';

export type ProvenanceSource =
  | 'live'
  | 'simulator'
  | 'seed'
  | 'imported'
  | 'manual'
  | 'replay'
  | 'unknown'
  | 'empty'
  | 'not-implemented'
  | 'not-connected';

export interface ProvenanceBadgeProps {
  /** Where the data shown on this view originated. */
  source: ProvenanceSource;
  /** Optional override tooltip; defaults to a description for the source. */
  tooltip?: string;
  /** Compact mode hides the label, showing only the icon. */
  compact?: boolean;
  className?: string;
}

type Variant = React.ComponentProps<typeof Badge>['variant'];

interface SourceMeta {
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  variant: Variant;
  defaultTooltip: string;
}

const SOURCE_META: Record<ProvenanceSource, SourceMeta> = {
  live: {
    label: 'Live',
    Icon: Radio,
    variant: 'success',
    defaultTooltip: 'Streaming from a live plant connection.',
  },
  simulator: {
    label: 'Sim',
    Icon: FlaskConical,
    variant: 'info',
    defaultTooltip: 'Generated by the in-app simulator (not a real plant).',
  },
  seed: {
    label: 'Seed',
    Icon: Database,
    variant: 'muted',
    defaultTooltip: 'Seeded reference data (no live source connected).',
  },
  imported: {
    label: 'Imported',
    Icon: FileInput,
    variant: 'muted',
    defaultTooltip: 'Imported from an external provider or upload.',
  },
  manual: {
    label: 'Manual',
    Icon: User,
    variant: 'warning',
    defaultTooltip: 'Manually entered by an operator.',
  },
  replay: {
    label: 'Replay',
    Icon: Rewind,
    variant: 'info',
    defaultTooltip: 'Replayed from historian or offline buffer.',
  },
  unknown: {
    label: 'Unknown',
    Icon: HelpCircle,
    variant: 'muted',
    defaultTooltip:
      'No provenance reported by the server. The data source could not be determined — treat the contents as untrusted until a real source is connected.',
  },
  empty: {
    label: 'Empty',
    Icon: HelpCircle,
    variant: 'muted',
    defaultTooltip:
      'No rows for this tenant. Connect a real data source or run the simulator to populate this view.',
  },
  'not-implemented': {
    label: 'Not impl.',
    Icon: HelpCircle,
    variant: 'warning',
    defaultTooltip:
      'This endpoint is registered but not yet backed by a real implementation.',
  },
  'not-connected': {
    label: 'Not connected',
    Icon: HelpCircle,
    variant: 'muted',
    defaultTooltip:
      'No live source is connected for this view. Empty data is the correct empty-state until a source is wired in.',
  },
};

/**
 * Tyndo ProvenanceBadge — distinguishes seed / sim / live / imported /
 * manual / replay data sources on operational views.
 *
 * Surfaces "where did this number come from?" without taking up the chrome
 * a `LiveTag` or `PLCStatus` consumes. Use one per view (in PageHeader meta
 * slot) or per-row when sources mix.
 */
export function ProvenanceBadge({
  source,
  tooltip,
  compact,
  className,
}: ProvenanceBadgeProps) {
  const meta = SOURCE_META[source];
  const { Icon } = meta;
  const tip = tooltip ?? meta.defaultTooltip;

  const badge = (
    <Badge
      variant={meta.variant}
      className={cn(
        'gap-1 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider',
        compact && 'px-1.5',
        className
      )}
      aria-label={`Data source: ${meta.label}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {!compact && <span>{meta.label}</span>}
    </Badge>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="top">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
